import { Injectable } from '@angular/core';
import { generateClient } from 'aws-amplify/api';
import * as queries from '@graphql/queries';
import * as mutations from '@graphql/mutations';
import {
    CreateLandingProblemSolutionBlockInput,
    DeleteLandingProblemSolutionBlockInput,
    LandingProblemSolutionBlock,
    ModelLandingProblemSolutionBlockFilterInput,
    UpdateLandingProblemSolutionBlockInput
} from '@app/core/services/api/API.service';

@Injectable({
    providedIn: 'root'
})
export class LandingProblemSolutionBlockService {
    private amplifyClient = generateClient();
    private _cachedBlocks: Map<
        string,
        LandingProblemSolutionBlock[]
    > = new Map();
    private _isLoading: Map<string, boolean> = new Map();
    private _loadingPromises: Map<
        string,
        Promise<LandingProblemSolutionBlock[]>
    > = new Map();

    private _getCacheKey(
        filter: ModelLandingProblemSolutionBlockFilterInput
    ): string {
        return JSON.stringify(filter || {});
    }

    async listLandingProblemSolutionBlocks(
        filter: ModelLandingProblemSolutionBlockFilterInput = null
    ): Promise<LandingProblemSolutionBlock[]> {
        const cacheKey = this._getCacheKey(filter);

        if (this._cachedBlocks.has(cacheKey)) {
            return this._cachedBlocks.get(cacheKey);
        }

        if (
            this._isLoading.get(cacheKey) &&
            this._loadingPromises.has(cacheKey)
        ) {
            return this._loadingPromises.get(cacheKey);
        }

        this._isLoading.set(cacheKey, true);
        const promise = this._fetchBlocks(filter, cacheKey);
        this._loadingPromises.set(cacheKey, promise);

        return promise;
    }

    private async _fetchBlocks(
        filter: ModelLandingProblemSolutionBlockFilterInput,
        cacheKey: string
    ): Promise<LandingProblemSolutionBlock[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.listLandingProblemSolutionBlocks,
                    variables: { filter }
                })
            ).data.listLandingProblemSolutionBlocks.items;

            if (data) {
                const blocks = data as LandingProblemSolutionBlock[];
                this._cachedBlocks.set(cacheKey, blocks);
                this._isLoading.set(cacheKey, false);
                this._loadingPromises.delete(cacheKey);
                return blocks;
            }
            this._isLoading.set(cacheKey, false);
            this._loadingPromises.delete(cacheKey);
            return null;
        } catch (error) {
            console.error(
                '[LandingProblemSolutionBlock] listLandingProblemSolutionBlocks failed:',
                error
            );
            this._isLoading.set(cacheKey, false);
            this._loadingPromises.delete(cacheKey);
            return null;
        }
    }

    clearCache(): void {
        this._cachedBlocks.clear();
        this._isLoading.clear();
        this._loadingPromises.clear();
    }

    async getLandingProblemSolutionBlock(
        id: string
    ): Promise<LandingProblemSolutionBlock> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.getLandingProblemSolutionBlock,
                    variables: { id }
                })
            ).data.getLandingProblemSolutionBlock;
            if (data) {
                return data as LandingProblemSolutionBlock;
            }
        } catch (error) {
            console.error('Error finding LandingProblemSolutionBlock:', error);
        }
        return null;
    }

    async createLandingProblemSolutionBlock(
        input: CreateLandingProblemSolutionBlockInput
    ): Promise<LandingProblemSolutionBlock> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.createLandingProblemSolutionBlock,
                    variables: { input }
                })
            ).data.createLandingProblemSolutionBlock;
            if (data) {
                this.clearCache();
                return data as LandingProblemSolutionBlock;
            }
        } catch (error) {
            console.error('Error creating LandingProblemSolutionBlock:', error);
        }
        return null;
    }

    async updateLandingProblemSolutionBlock(
        input: UpdateLandingProblemSolutionBlockInput
    ): Promise<LandingProblemSolutionBlock> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.updateLandingProblemSolutionBlock,
                    variables: { input }
                })
            ).data.updateLandingProblemSolutionBlock;
            if (data) {
                this.clearCache();
                return data as LandingProblemSolutionBlock;
            }
        } catch (error) {
            console.error('Error updating LandingProblemSolutionBlock:', error);
        }
        return null;
    }

    async deleteLandingProblemSolutionBlock(
        input: DeleteLandingProblemSolutionBlockInput
    ): Promise<LandingProblemSolutionBlock> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.deleteLandingProblemSolutionBlock,
                    variables: { input }
                })
            ).data.deleteLandingProblemSolutionBlock;
            if (data) {
                this.clearCache();
                return data as LandingProblemSolutionBlock;
            }
        } catch (error) {
            console.error('Error deleting LandingProblemSolutionBlock:', error);
        }
        return null;
    }
}
